import { signal, computed } from '@preact/signals';
import type { ActionItem } from '../types/ai';
import { apiFetch } from '../services/api';
import { showToast } from './ui.store';
import { normalizeText } from '../utils/formatters';

export const isKanbanOpenSignal = signal<boolean>(false);
export const kanbanTasksSignal = signal<ActionItem[]>([]);
export const isLoadingKanbanSignal = signal<boolean>(false);
export const selectedKanbanTaskSignal = signal<ActionItem | null>(null);

export const kanbanFilterStakeholderSignal = signal<string>('all');
export const kanbanFilterPrioritySignal = signal<string>('all');
export const kanbanFilterDueDateSignal = signal<string>('all');
export const kanbanFilterAssigneeSignal = signal<'mine' | 'all'>('all');
export const kanbanSearchQuerySignal = signal<string>('');

export async function loadAllKanbanTasks() {
  try {
    isLoadingKanbanSignal.value = true;
    const tasks = await apiFetch<ActionItem[]>('/ai/action-items/all');
    kanbanTasksSignal.value = Array.isArray(tasks) ? tasks : [];
  } catch (err: any) {
    console.error('[KanbanStore] Erro ao carregar tarefas do Kanban:', err);
    showToast('Erro ao carregar quadro de tarefas.', 'error');
  } finally {
    isLoadingKanbanSignal.value = false;
  }
}

export async function updateKanbanTaskStatus(taskId: number, newStatus: 'pending' | 'in_progress' | 'completed' | 'cancelled') {
  const previousTasks = [...kanbanTasksSignal.value];

  // Atualização otimista no front-end
  kanbanTasksSignal.value = kanbanTasksSignal.value.map(t =>
    t.id === taskId ? { ...t, status: newStatus } : t
  );

  if (selectedKanbanTaskSignal.value?.id === taskId) {
    selectedKanbanTaskSignal.value = { ...selectedKanbanTaskSignal.value, status: newStatus };
  }

  try {
    await apiFetch(`/ai/action-items/${taskId}/details`, {
      method: 'PUT',
      body: JSON.stringify({ status: newStatus })
    });
  } catch (err: any) {
    console.error('[KanbanStore] Erro ao atualizar status da tarefa:', err);
    kanbanTasksSignal.value = previousTasks;
    showToast('Falha ao atualizar status da tarefa no servidor.', 'error');
  }
}

export async function updateKanbanTaskDetails(taskId: number, data: {
  description?: string;
  detailed_description?: string;
  notes?: string;
  links?: string[] | string;
  priority?: 'low' | 'medium' | 'high';
  assignee?: string;
  due_date?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'cancelled';
}) {
  const previousTasks = [...kanbanTasksSignal.value];

  kanbanTasksSignal.value = kanbanTasksSignal.value.map(t =>
    t.id === taskId ? { ...t, ...data } : t
  );

  if (selectedKanbanTaskSignal.value?.id === taskId) {
    selectedKanbanTaskSignal.value = { ...selectedKanbanTaskSignal.value, ...data };
  }

  try {
    await apiFetch(`/ai/action-items/${taskId}/details`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    showToast('Informações da tarefa salvas!', 'success');
  } catch (err: any) {
    console.error('[KanbanStore] Erro ao salvar detalhes da tarefa:', err);
    kanbanTasksSignal.value = previousTasks;
    showToast('Falha ao salvar detalhes da tarefa.', 'error');
  }
}

export async function deleteKanbanTask(taskId: number) {
  const previousTasks = [...kanbanTasksSignal.value];

  // Remoção otimista do sinal global
  kanbanTasksSignal.value = kanbanTasksSignal.value.filter(t => t.id !== taskId);

  if (selectedKanbanTaskSignal.value?.id === taskId) {
    selectedKanbanTaskSignal.value = null;
  }

  try {
    await apiFetch(`/ai/action-items/${taskId}`, {
      method: 'DELETE'
    });
    showToast('Tarefa excluída permanentemente com sucesso!', 'success');
  } catch (err: any) {
    console.error('[KanbanStore] Erro ao excluir tarefa:', err);
    kanbanTasksSignal.value = previousTasks;
    showToast('Falha ao excluir tarefa do servidor.', 'error');
  }
}

export const filteredKanbanTasks = computed(() => {
  let list = kanbanTasksSignal.value;
  const stakeholder = kanbanFilterStakeholderSignal.value;
  const priority = kanbanFilterPrioritySignal.value;
  const dueDateFilter = kanbanFilterDueDateSignal.value;
  const assigneeFilter = kanbanFilterAssigneeSignal.value;
  const query = normalizeText(kanbanSearchQuerySignal.value);

  // 1. Filtro de Busca Textual
  if (query) {
    list = list.filter(t => {
      const descNorm = normalizeText(t.description || '');
      const notesNorm = normalizeText(t.notes || '');
      const assigneeNorm = normalizeText(t.assignee || '');
      const stNameNorm = normalizeText(t.stakeholder_name || '');
      return descNorm.includes(query) || notesNorm.includes(query) || assigneeNorm.includes(query) || stNameNorm.includes(query);
    });
  }

  // 2. Filtro por Stakeholder (Telefone/Origem)
  if (stakeholder !== 'all') {
    list = list.filter(t => t.contact_phone === stakeholder);
  }

  // 3. Filtro por Prioridade
  if (priority !== 'all') {
    list = list.filter(t => (t.priority || 'medium') === priority);
  }

  // 4. Filtro Minhas vs Todos
  if (assigneeFilter === 'mine') {
    list = list.filter(t => {
      const ass = (t.assignee || '').toLowerCase();
      return ass.includes('você') || ass.includes('voce') || ass.includes('admin') || ass.includes('me') || ass === '';
    });
  }

  // 5. Filtro por Prazo
  if (dueDateFilter !== 'all') {
    const today = new Date().toISOString().split('T')[0];
    list = list.filter(t => {
      if (!t.due_date) return dueDateFilter === 'no_due_date';
      const dueStr = t.due_date.split('T')[0];
      if (dueDateFilter === 'overdue') return dueStr < today && t.status !== 'completed';
      if (dueDateFilter === 'today') return dueStr === today;
      if (dueDateFilter === 'upcoming') return dueStr > today;
      return true;
    });
  }

  return list;
});
